/* eslint-disable @typescript-eslint/no-unused-vars */
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { CircularProgress, Dialog, DialogContent } from '@mui/material';
import { KJUR, KEYUTIL, RSAKey } from 'jsrsasign';

import FileUploadContent from './FileUploadContent';
import CombineFilesContent from './CombineFilesContent';

import { CombineDataType, ProjectDataFile, Sheet } from '@DStypes/project';
import {
  useCancelCombineMutation,
  useConvertToGsheetMutation,
  useLazyGetCombineFilesDetailsQuery
} from '@api/projects';
import RoutesPath from '@constants/RoutesPath';
import PlanBanner from '@components/PlanBanner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/store';
import MDTypography from '@components/MDTypography';
import MDBox from '@components/MDBox';
import {
  useExchangeJWTForAccessTokenMutation,
  useLazyGetSheetDetailsQuery
} from '@api/spreadsheet';
import SheetSelector from './SheetSelector';
import { atob } from 'js-base64';
import { CONNECT_GOOGLE_SHEET, SELECTED_A_SHEET } from '@constants/eventNames';
import { sendAmplitudeData } from '@utils/amplitude';
import { setUserNotifications } from '@components/Notification/slice';
import { MSG_TYPE } from '@DStypes/notifications';
import {
  GOOGLE_SHEET_PERMISSION_DENIED,
  GOOGLE_SHEET_UNKNOWN_ISSUE_OCCURRED,
  INVALID_GOOGLE_SHEET
} from '@constants/messages';
import CloseButton from '@components/CloseButton';
import UserAuth from '@services/auth';
import useUserDetails from '@hooks/useUserDetails';

const FileUploadModal = (props: any) => {
  const {
    open,
    onClose,
    handleFileUploadSuccess = () => null,
    isAddData = false,
    isCombineReady = false,
    projectDataFiles,
    setIsCombineReady,
    setInitiatePolling,
    initFileType = ''
  } = props;

  const dispatch = useDispatch();

  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const planValidityText = useSelector(
    (state: RootState) => state?.AppData.planValidityText
  );
  const user = UserAuth.userInfo;

  const { getUserDetails } = useUserDetails();

  const projectBoard = useSelector((state: RootState) => state?.projectBoard);

  const [combineData, setCombineData] = useState<CombineDataType>();

  const [pollingInterval, setPollingInterval] = useState(0);
  const [showPlanUpgradeBanner, setShowPlanUpgradeBanner] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState('');
  const [isValidSpreadsheet, setIsValidSpreadsheet] = useState<boolean>(false);
  const [spreadsheetLink, setSpreadsheetLink] = useState<string>('');
  const [startUpload, setStartUpload] = useState<boolean>(false);
  const [selectedSheetForUpload, setSelectedSheetforUpload] =
    useState<string>('');
  const [showSheetSelector, setShowSheetSelector] = useState<boolean>(false);
  const [gSheetName, setGsheetName] = useState('');
  const [xlsxFileId, setxlsxFileId] = useState('');

  const [getGoogleSpreadsheetDetails, { data: spreadsheetMetaData }] =
    useLazyGetSheetDetailsQuery();
  const [exchangeToken] = useExchangeJWTForAccessTokenMutation();

  const [cancelCombine, { isSuccess: isCancelSuccess }] =
    useCancelCombineMutation();

  const [convertToGsheet, { data: xlsxMetaData }] =
    useConvertToGsheetMutation();

  const handleCancelCombine = async () => {
    await cancelCombine({
      file_id:
        combineData?.source_datafile.id ||
        projectBoard?.currentProjectData?.project_datafiles?.[0]?.id,
      project_id: projectBoard?.currentProjectData?.id
    });
    setIsCombineReady(false);
    onClose();
  };

  const onClickOutside = () => {
    (pollingInterval || isCombineReady) && handleCancelCombine();
    onClose();
  };

  const [getCombineDataDetails, { data }] = useLazyGetCombineFilesDetailsQuery(
    pollingInterval
      ? {
          pollingInterval: pollingInterval
        }
      : {}
  );

  useEffect(() => {
    if (
      !window.location.href.includes(RoutesPath.MY_PROJECTS) &&
      planValidityText.value &&
      subscriptionFlag
    )
      setShowPlanUpgradeBanner(true);
    else setShowPlanUpgradeBanner(false);
  }, [planValidityText.value, subscriptionFlag]);

  useEffect(() => {
    if (data?.id) {
      setPollingInterval(0);
      setCombineData(data);
    }
  }, [data]);

  //Hook to fetch combine details on reload or revisit
  useEffect(() => {
    const activeDataFiles = projectDataFiles?.filter(
      (dataFile: ProjectDataFile) => dataFile.is_active === true
    );

    if (isCombineReady && activeDataFiles.length > 1 && !uploadedFileId) {
      getCombineDataDetails({
        id: activeDataFiles[activeDataFiles?.length - 1]?.id
      });
      setPollingInterval(500);
    }
  }, [isCombineReady, uploadedFileId]);

  const handleGoogleSheetUpload = async (spreadsheetLink: string) => {
    const spreadsheetIdRegex = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;
    const sheetIDRegex = /gid=\d+/;
    const spreadsheetId = spreadsheetLink.includes(
      'https://docs.google.com/spreadsheets'
    )
      ? spreadsheetLink.match(spreadsheetIdRegex)?.[1] ?? ''
      : spreadsheetLink;
    const googleSheetId = Number(
      spreadsheetLink.match(sheetIDRegex)?.[0].slice(4)
    );

    let isXlsx = false;

    //Processing for xlsx g-sheet files
    const xlsxSheetLinkRegex =
      /https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9_-]{33}\/?(edit#gid=0|edit\?usp=sharing)?\/?/;
    const xlsxIdRegex = /d\/[a-zA-Z0-9_-]{33}\//;
    const unformattedXlsxId = spreadsheetLink.match(xlsxIdRegex)?.[0];
    const xlsxId = unformattedXlsxId?.substring(
      2,
      unformattedXlsxId?.length - 1
    );

    if (xlsxSheetLinkRegex.test(spreadsheetLink) && xlsxId?.length === 33) {
      isXlsx = true;
      setxlsxFileId(xlsxId);
    }

    const header = { alg: 'RS256', typ: 'JWT' };

    const payload = {
      iss: process.env.REACT_APP_SERVICE_ACCOUNT_EMAIL,
      aud: 'https://oauth2.googleapis.com/token',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 1 * 60,
      scope: 'https://www.googleapis.com/auth/spreadsheets.readonly'
    };

    const rsaKey = KEYUTIL.getKey(
      atob(process.env.REACT_APP_SERVICE_ACCOUNT_SECRET_KEY ?? '')
    );

    const jwtToken = KJUR.jws.JWS.sign(
      'RS256',
      JSON.stringify(header),
      JSON.stringify(payload),
      rsaKey as RSAKey
    );

    let spreadsheetAccessResponse: any;

    if (!isXlsx) {
      const response = await exchangeToken(jwtToken).unwrap();

      spreadsheetAccessResponse = await getGoogleSpreadsheetDetails({
        spreadsheet_id: spreadsheetId,
        access_token: response?.access_token ?? ''
      });
    } else {
      spreadsheetAccessResponse = await convertToGsheet({ file_id: xlsxId });
    }

    if (
      spreadsheetAccessResponse?.error &&
      'status' in spreadsheetAccessResponse.error
    ) {
      const errorMessage =
        spreadsheetAccessResponse.error.status === 403
          ? GOOGLE_SHEET_PERMISSION_DENIED
          : spreadsheetAccessResponse.error.status === 404
          ? INVALID_GOOGLE_SHEET
          : GOOGLE_SHEET_UNKNOWN_ISSUE_OCCURRED;

      dispatch(
        setUserNotifications({
          created_at: dayjs(new Date())
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          is_read: false,
          is_silent: false,
          is_user_message: true,
          message_text: errorMessage,
          message_data: JSON.stringify({
            chart_id: '',
            project: {
              id: '',
              name: ''
            }
          }),
          updated_at: dayjs(new Date())
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          message_type: MSG_TYPE.ERROR,
          is_important: false,
          recipient_obj: 0
        })
      );

      sendAmplitudeData(CONNECT_GOOGLE_SHEET, { Status: 'Fail' });
      setIsValidSpreadsheet(false);

      return;
    } else if (
      !isNaN(googleSheetId) &&
      spreadsheetAccessResponse?.data &&
      spreadsheetAccessResponse?.data?.sheets?.length === 1
    ) {
      setGsheetName(
        isXlsx
          ? spreadsheetAccessResponse?.data?.file_name
          : spreadsheetAccessResponse?.data?.properties.title || ''
      );
      setSelectedSheetforUpload(
        spreadsheetAccessResponse?.data?.sheets[0].properties.title
      );
      setStartUpload(true);
    } else if (
      !isNaN(googleSheetId) &&
      spreadsheetAccessResponse?.data &&
      spreadsheetAccessResponse?.data?.sheets?.length > 1
    ) {
      setGsheetName(
        isXlsx
          ? spreadsheetAccessResponse?.data?.file_name
          : spreadsheetAccessResponse?.data?.properties.title || ''
      );

      setSelectedSheetforUpload(
        spreadsheetAccessResponse.data?.sheets?.filter(
          (sheet: Sheet) =>
            sheet?.properties?.sheetId === googleSheetId ||
            sheet?.properties?.sheetId
              .toString()
              .startsWith(googleSheetId.toString())
        )[0]?.properties?.title ??
          spreadsheetAccessResponse?.data?.sheets[0].properties.title
      );

      setShowSheetSelector(true);
    } else if (
      spreadsheetAccessResponse?.data &&
      spreadsheetAccessResponse?.data?.sheets?.length === 1
    ) {
      setGsheetName(
        isXlsx
          ? spreadsheetAccessResponse?.data?.file_name
          : spreadsheetAccessResponse?.data?.properties.title || ''
      );
      setSelectedSheetforUpload(
        spreadsheetAccessResponse?.data?.sheets[0].properties.title
      );
      setStartUpload(true);
    } else {
      setGsheetName(
        isXlsx
          ? spreadsheetAccessResponse?.data?.file_name
          : spreadsheetAccessResponse?.data?.properties.title || ''
      );
      setSelectedSheetforUpload(
        spreadsheetAccessResponse?.data?.sheets[0]?.properties?.title || ''
      );
      setShowSheetSelector(true);
    }
    sendAmplitudeData(CONNECT_GOOGLE_SHEET, { Status: 'Success' });
  };

  const handleClickOkButton = async () => {
    if (spreadsheetMetaData || xlsxMetaData) setStartUpload(true);

    // handleGoogleSheetUpload(spreadsheetLink);

    sendAmplitudeData(SELECTED_A_SHEET, {
      file_type: 'g-sheet',
      sheet_name: selectedSheetForUpload
    });
  };

  useEffect(
    () => () => {
      const userId = user?.id;

      if (UserAuth.isAuthenticated && userId) getUserDetails(userId as string);
    },
    []
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={pollingInterval ? () => null : () => onClickOutside()}
        sx={{ zIndex: 80 }}
        PaperProps={{
          sx: {
            width: isCombineReady ? '80%' : '430px',
            height: 'max-content',
            maxWidth: isCombineReady ? '1250px' : '1050px',
            position: 'relative',
            overflow: 'visible'
          }
        }}
      >
        <CloseButton
          onClose={() => {
            pollingInterval ? null : onClickOutside();
          }}
        />
        {/* {showPlanUpgradeBanner && !(pollingInterval || isCombineReady) && (
          <PlanBanner
            planValidityText={planValidityText}
            setShowPlanUpgradeBanner={setShowPlanUpgradeBanner}
            isUploadModal={true}
          />
        )} */}
        {pollingInterval || isCombineReady ? (
          <CombineFilesContent
            combineData={combineData}
            setInitiatePolling={setInitiatePolling}
            handleCancelCombine={handleCancelCombine}
            isCancelSuccess={isCancelSuccess}
          />
        ) : (
          <>
            <FileUploadContent
              handleFileUploadSuccess={handleFileUploadSuccess}
              onClose={onClose}
              isAddData={isAddData}
              startUpload={startUpload}
              onGetCombineDetails={(fileId) => {
                if (fileId) {
                  getCombineDataDetails({ id: fileId });
                  setPollingInterval(500);
                  setUploadedFileId(fileId);
                  setIsCombineReady(true);
                }
              }}
              setIsValidSpreadsheet={setIsValidSpreadsheet}
              setSpreadsheetLink={setSpreadsheetLink}
              uploadSpreadsheetPayload={{
                file_name: gSheetName,
                [spreadsheetLink.includes(
                  'https://docs.google.com/spreadsheets'
                ) && !xlsxFileId
                  ? 'sheet_link'
                  : 'sheet_id']: xlsxFileId
                  ? xlsxMetaData?.gsheet_file_id
                  : spreadsheetLink,
                sheet_name: selectedSheetForUpload,
                project_id: isAddData
                  ? projectBoard?.currentProjectData?.id
                  : undefined,
                xlsx_id: xlsxFileId ? xlsxFileId : undefined
              }}
              handleGoogleSheetUpload={handleGoogleSheetUpload}
              initFileType={initFileType}
            />
            {isValidSpreadsheet &&
              (!spreadsheetMetaData && !xlsxMetaData ? (
                <Dialog
                  open
                  onClose={onClose}
                  fullWidth
                  sx={{
                    zIndex: 200,
                    '& .MuiPaper-root': {
                      width: '700px',
                      maxWidth: '800px',
                      position: 'relative'
                    }
                  }}
                >
                  <DialogContent sx={{ p: 6, textAlign: 'center' }}>
                    <MDTypography variant='h5' color='info'>
                      Thank you for providing READ-ONLY access to your
                      spreadsheet.
                    </MDTypography>
                    <MDTypography variant='h5' color='info'>
                      Please wait while we process your data.
                    </MDTypography>
                    <MDBox py={3} display={'flex'} justifyContent={'center'}>
                      <CircularProgress />
                    </MDBox>
                    <MDTypography
                      variant='h5'
                      color='info'
                      sx={{ textAlign: 'center' }}
                    >
                      This may take upto a minute. Avoid refreshing or
                      cancelling. Thank you!
                    </MDTypography>
                  </DialogContent>
                </Dialog>
              ) : (
                showSheetSelector && (
                  <SheetSelector
                    spreadsheetMetaData={
                      xlsxFileId ? xlsxMetaData : spreadsheetMetaData
                    }
                    handleClickOkButton={handleClickOkButton}
                    setSelectedSheetforUpload={setSelectedSheetforUpload}
                    onClose={() => onClickOutside()}
                    initSheet={selectedSheetForUpload}
                  />
                )
              ))}
          </>
        )}
      </Dialog>
    </>
  );
};

export default FileUploadModal;
